/**
 * @author Fernando Ruano
 * @file index.js
 * @description Modal Input Blood Pressure Component
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, DatePicker, Row, Col, Input, TimePicker } from 'antd';
import moment from 'moment';

// Components
import ButtonFilled from '@nephro/components/ButtonFilled';
import ButtonOutlined from '@nephro/components/ButtonOutlined';

// Helpers
import {
  disabledFutureDates,
  disabledFutureHours,
  disabledFutureMinuts
} from 'commons/helpers/utils';

// Styles
import styles from '../../styles.module.css';

/**
 * Modal Input Blood Pressure Component.
 * @return { Node } React Modal Input Blood Pressure Component
 */
const ModalInputsBP = ({
  isEditing,
  visible,
  setVisible,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  systolic,
  setSystolic,
  diastolic,
  setDiastolic,
  onConfirmCreate
}) => {
  const { t: translate } = useTranslation();
  const validateInputs =
    !diastolic ||
    !systolic ||
    !selectedTime ||
    !selectedDate ||
    diastolic.length < 2 ||
    systolic.length < 2;

  const inputLabel = (string) => (
    <h2 className="mt-6 text-sm text-baxterGray text-left">
      {translate(string)}
    </h2>
  );

  return (
    <Modal
      className="records-menu"
      visible={visible}
      title=""
      closable={false}
      footer={null}>
      <div className="text-center">
        <h2 className="mb-0 mt-4 text-2xl font-medium text-baxterGray">
          {translate(
            isEditing ? 'patientRecords.update' : 'patientRecords.create'
          )}
        </h2>
        <h2 className="mb-5 mt-4 text-sm text-baxterGray">
          {translate(
            isEditing
              ? 'patientRecords.updateMeasureBP'
              : 'patientRecords.recordMeasureBP'
          )}
        </h2>

        <Row gutter={10}>
          <Col xs={12}>
            {inputLabel('patientRecords.date')}
            <DatePicker
              disabledDate={disabledFutureDates}
              value={selectedDate}
              defaultValue={selectedDate}
              format={'DD/MM/YYYY'}
              onChange={(val) => setSelectedDate(val)}
            />
          </Col>
          <Col xs={12}>
            {inputLabel('Hora')}
            <TimePicker
              showNow={false}
              disabledDate={
                selectedDate?.format('DD-MM-YYYY') ===
                moment().format('DD-MM-YYYY')
                  ? disabledFutureDates
                  : ''
              }
              disabledHours={
                selectedDate?.format('DD-MM-YYYY') ===
                moment().format('DD-MM-YYYY')
                  ? disabledFutureHours
                  : ''
              }
              disabledMinutes={
                selectedDate?.format('DD-MM-YYYY') ===
                moment().format('DD-MM-YYYY')
                  ? disabledFutureMinuts
                  : ''
              }
              value={selectedTime || ''}
              use12Hours
              placeholder={translate('patientRecords.select')}
              format="h:mm a"
              onChange={(val) => {
                if (
                  selectedDate?.format('DD-MM-YYYY') !==
                  moment()?.format('DD-MM-YYYY')
                ) {
                  setSelectedTime(val);
                } else if (selectedDate < moment()) {
                  setSelectedTime(val);
                }
              }}
            />
          </Col>
          <Col xs={11}>
            <>
              {inputLabel('patientRecords.systolic')}
              <Input
                type="number"
                value={systolic}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value.length <= 3) setSystolic(value);
                }}
              />
            </>
          </Col>
          <Col className={styles.columnMiddle} xs={2}>
            <h2 className="text-sm text-baxterGray text-left">/</h2>
          </Col>
          <Col xs={11}>
            <>
              {inputLabel('patientRecords.diastolic')}
              <Input
                type="number"
                value={diastolic}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value.length <= 3) setDiastolic(value);
                }}
              />
            </>
          </Col>
          {(systolic?.length < 2 || diastolic?.length < 2) && (
            <p className="text-error mt-3 text-left">
              {translate('patientRecords.lengthError')}
            </p>
          )}
        </Row>
        <div className="w-full flex flex-row justify-center mt-10">
          <ButtonOutlined
            key="cancel"
            type="cyan"
            textButton={translate('commons.cancel')}
            classStyles={[styles.actionButton, 'mr-5'].join(' ')}
            onActionClick={() => setVisible(false)}
          />
          <ButtonFilled
            disabled={validateInputs}
            key="submit"
            type="cyan"
            textButton={translate(
              !isEditing ? 'patientRecords.creation' : 'patientRecords.update'
            )}
            classStyles={[
              styles.actionButton,
              `${validateInputs && 'opacity-40'}`
            ].join(' ')}
            onActionClick={onConfirmCreate}
          />
        </div>
      </div>
    </Modal>
  );
};

/**
 * @name ModalInputsBP.propTypes
 * @type { Object }
 */
ModalInputsBP.propTypes = {
  isEditing: PropTypes.bool,
  visible: PropTypes.bool,
  setVisible: PropTypes.func,
  selectedDate: PropTypes.any,
  setSelectedDate: PropTypes.func,
  selectedTime: PropTypes.any,
  setSelectedTime: PropTypes.func,
  systolic: PropTypes.any,
  setSystolic: PropTypes.func,
  diastolic: PropTypes.any,
  setDiastolic: PropTypes.func,
  onConfirmCreate: PropTypes.func
};

/**
 * Default props
 */
ModalInputsBP.defaultProps = {
  mode: 'confirm',
  isEditing: false,
  selectedDate: moment()
};

export default ModalInputsBP;
